// Equipment step component - Step 4 of character creation wizard

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import {
  computeCurrentGoldCopper,
  computeCurrentInventory,
  computeStartingGoldCopper,
  enrichEquipmentWithInventory,
  groupEquipmentByCategory,
  isProficient,
} from '../../lib/equipment-utils.js';
import { formatCurrency, parseCostToCopper, slugToTitle } from '../../lib/format.js';
import { MASTERY_DESCRIPTIONS } from './weapon-option-card.js';

/**
 * @typedef {import('../views/create-character-view.js').EquipmentPackage} EquipmentPackage
 * @typedef {import('../views/create-character-view.js').EquipmentItem} EquipmentItem
 */

/**
 * Equipment buy/sell step component.
 * Controlled component: receives all data as properties, emits events for mutations.
 *
 * Events: sell-item, buy-item, reset-changes
 */
class EquipmentStep extends BaseComponent {
  static properties = {
    classEquipmentPackages: { type: Array },
    backgroundEquipmentPackages: { type: Array },
    equipmentPackageId: { type: String },
    backgroundEquipmentPackageId: { type: String },
    backgroundToolChoice: { type: String },
    equipmentChanges: { type: Object },
    equipment: { type: Array },
    weaponProficiencies: { type: Array },
    armorTraining: { type: Array },
    weaponMasterySelections: { type: Array },
    equipmentLoading: { type: Boolean },
    isHeaderStuck: { type: Boolean, attribute: 'is-header-stuck' },
    // internal
    _searchQuery: { type: String, state: true },
    _categoryFilter: { type: String, state: true },
    _inventoryOnly: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .sticky-header {
      position: sticky;
      top: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      z-index: 10;
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      margin-top: -0.5rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }

    .sticky-header.stuck {
      border-bottom-color: rgba(0, 0, 0, 0.125);
      padding-top: 1rem;
    }

    .sticky-header .btn {
      background-color: white;
    }

    .filter-bar {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .filter-bar input {
      width: 14rem;
      flex-shrink: 0;
    }

    .filter-bar .btn-group {
      margin-left: auto;
    }

    .equipment-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.5rem;
    }

    @media (max-width: 991px) {
      .equipment-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 575px) {
      .equipment-grid {
        grid-template-columns: 1fr;
      }
    }

    .equipment-card {
      border: 1px solid #dee2e6;
      border-radius: 0.375rem;
      padding: 0.5rem 0.625rem;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    .equipment-card.owned {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }

    .card-top-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.375rem;
    }

    .card-item-name {
      font-size: 0.875rem;
      font-weight: 500;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .card-actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      flex-shrink: 0;
    }

    .qty-badge {
      background: #6c757d;
      color: white;
      font-size: 0.875rem;
      font-weight: 600;
      padding: 0.15rem 0.45rem;
      border-radius: 0.2rem;
      min-width: 1rem;
      text-align: center;
      margin-left: 0.375rem;
      vertical-align: middle;
    }

    .card-stats {
      font-size: 0.875rem;
      color: #6c757d;
    }

    .card-cost-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.25rem;
    }

    .card-cost {
      font-size: 0.875rem;
      color: #6c757d;
    }

    .not-proficient-icon {
      color: #dc3545;
      font-size: 1.25rem;
      cursor: default;
    }

    .mastery-info-icon {
      color: #6c757d;
      cursor: default;
      vertical-align: middle;
    }
  `;

  constructor() {
    super();
    /** @type {Array<EquipmentPackage>} */
    this.classEquipmentPackages = [];
    /** @type {Array<EquipmentPackage>} */
    this.backgroundEquipmentPackages = [];
    this.equipmentPackageId = '';
    this.backgroundEquipmentPackageId = '';
    this.backgroundToolChoice = '';
    /** @type {{soldItems: Array<{slug: string, name?: string, cost?: string, quantity: number}>, purchasedItems: Array<{slug: string, name?: string, cost?: string, quantity: number}>}} */
    this.equipmentChanges = { soldItems: [], purchasedItems: [] };
    /** @type {Array<EquipmentItem>} */
    this.equipment = [];
    /** @type {Array<string>} */
    this.weaponProficiencies = [];
    /** @type {Array<string>} */
    this.armorTraining = [];
    /** @type {Array<string>} */
    this.weaponMasterySelections = [];
    this.equipmentLoading = false;
    this.isHeaderStuck = false;
    this._searchQuery = '';
    this._categoryFilter = 'all';
    this._inventoryOnly = false;
  }

  get _startingGoldCopper() {
    return computeStartingGoldCopper(
      this.classEquipmentPackages,
      this.equipmentPackageId,
      this.backgroundEquipmentPackages,
      this.backgroundEquipmentPackageId,
    );
  }

  get _currentGoldCopper() {
    return computeCurrentGoldCopper(this._startingGoldCopper, this.equipmentChanges, this.equipment);
  }

  get _currentInventory() {
    return computeCurrentInventory(
      this.classEquipmentPackages,
      this.equipmentPackageId,
      this.backgroundEquipmentPackages,
      this.backgroundEquipmentPackageId,
      this.backgroundToolChoice,
      this.equipmentChanges,
      this.equipment,
    );
  }

  get _equipmentByCategory() {
    return groupEquipmentByCategory(this.equipment, this._searchQuery);
  }

  get _enrichedEquipment() {
    return enrichEquipmentWithInventory(this._equipmentByCategory, this._currentInventory);
  }

  get _visibleCategories() {
    const enriched = this._enrichedEquipment;

    // Apply category filter
    /** @type {{[category: string]: Array<EquipmentItem & {ownedQuantity: number}>}} */
    let result;
    if (this._categoryFilter === 'all') {
      result = enriched;
    } else {
      result = {};
      const filtered = enriched[this._categoryFilter];
      if (filtered) {
        result[this._categoryFilter] = filtered;
      }
    }

    // Apply inventory toggle
    if (this._inventoryOnly) {
      /** @type {{[category: string]: Array<EquipmentItem & {ownedQuantity: number}>}} */
      const filtered = {};
      for (const [category, items] of Object.entries(result)) {
        const owned = items.filter((eq) => eq.ownedQuantity > 0);
        if (owned.length > 0) filtered[category] = owned;
      }
      return filtered;
    }

    return result;
  }

  get _categoryOptions() {
    const categories = Object.keys(this._equipmentByCategory);
    return [{ key: 'all', label: 'All' }, ...categories.map((cat) => ({ key: cat, label: slugToTitle(cat) }))];
  }

  render() {
    const goldDisplay = formatCurrency(this._currentGoldCopper);
    const isNegative = this._currentGoldCopper < 0;
    const hasChanges = this.equipmentChanges.soldItems.length > 0 || this.equipmentChanges.purchasedItems.length > 0;

    return html`
      <div>
        <div class="sticky-header ${this.isHeaderStuck ? 'stuck' : ''}">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">Buy and Sell Equipment</h5>
            <div class="d-flex align-items-center gap-2">
              <span class="badge ${isNegative ? 'bg-danger' : 'bg-success'} fs-6">${goldDisplay}</span>
              ${
                hasChanges
                  ? html`
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="${this._onReset}">
                      Reset
                    </button>
                  `
                  : ''
              }
            </div>
          </div>
        </div>
        <p class="text-muted mb-3">Buy and sell equipment. Items sell at full price.</p>

        ${
          this.equipmentLoading
            ? html`
              <div class="text-center py-3">
                <div class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading equipment...</span>
                </div>
              </div>
            `
            : html`
              <div class="filter-bar mb-3">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Search..."
                  .value="${this._searchQuery}"
                  @input="${(/** @type {Event} */ e) => {
                    this._searchQuery = /** @type {HTMLInputElement} */ (e.target).value;
                  }}"
                />
                <div class="btn-group btn-group-sm" role="group">
                  ${this._categoryOptions.map(
                    (opt) => html`
                      <button
                        type="button"
                        class="btn ${this._categoryFilter === opt.key ? 'btn-primary' : 'btn-outline-secondary'}"
                        @click="${() => {
                          this._categoryFilter = opt.key;
                        }}"
                      >
                        ${opt.label}
                      </button>
                    `,
                  )}
                </div>
                <button
                  type="button"
                  class="btn btn-sm ${this._inventoryOnly ? 'btn-primary' : 'btn-outline-secondary'}"
                  @click="${() => {
                    this._inventoryOnly = !this._inventoryOnly;
                  }}"
                >
                  Only My Inventory
                </button>
              </div>

              ${Object.entries(this._visibleCategories).map(
                ([category, items]) => html`
                  <div class="mb-4">
                    <h5 class="text-capitalize mb-2 mt-2">${slugToTitle(category)}</h5>
                    <div class="equipment-grid">
                      ${items.map((eq) => {
                        const cost = parseCostToCopper(eq.cost || '0 cp');
                        const canAfford = cost <= this._currentGoldCopper;
                        const owned = eq.ownedQuantity > 0;
                        const proficient = isProficient(eq, this.weaponProficiencies, this.armorTraining);
                        return html`
                          <div class="equipment-card ${owned ? 'owned' : ''}">
                            <div class="card-top-row">
                              <span class="card-item-name"
                                >${eq.name}${
                                  owned ? html` <span class="qty-badge">${eq.ownedQuantity}</span> ` : ''
                                }</span
                              >
                              <span class="card-actions">
                                ${
                                  owned
                                    ? html`
                                      <button
                                        class="btn btn-outline-danger btn-sm"
                                        @click="${() => this._onSell(eq.slug)}"
                                      >
                                        Sell${eq.bundlePricing && eq.bundleSize ? ` (${eq.bundleSize})` : ''}
                                      </button>
                                    `
                                    : ''
                                }
                                ${
                                  !canAfford
                                    ? html`
                                      <span data-bs-toggle="tooltip" data-bs-title="You don't have ${eq.cost}">
                                        <button class="btn btn-outline-primary btn-sm" disabled>
                                          Buy${eq.bundlePricing && eq.bundleSize ? ` (${eq.bundleSize})` : ''}
                                        </button>
                                      </span>
                                    `
                                    : html`
                                      <button
                                        class="btn btn-outline-primary btn-sm"
                                        @click="${() => this._onBuy(eq.slug)}"
                                      >
                                        Buy${eq.bundlePricing && eq.bundleSize ? ` (${eq.bundleSize})` : ''}
                                      </button>
                                    `
                                }
                              </span>
                            </div>
                            ${this._renderStatsLine(eq)}
                            <div class="card-cost-row">
                              <span class="card-cost">${eq.cost || '--'}</span>
                              ${
                                proficient === false
                                  ? html`
                                    <span
                                      class="not-proficient-icon"
                                      data-bs-toggle="tooltip"
                                      data-bs-title="Not proficient"
                                      ><i class="bi bi-exclamation-triangle"></i
                                    ></span>
                                  `
                                  : ''
                              }
                            </div>
                          </div>
                        `;
                      })}
                    </div>
                  </div>
                `,
              )}
            `
        }
      </div>
    `;
  }

  updated() {
    this._initTooltips();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeTooltips();
  }

  _initTooltips() {
    this._disposeTooltips();
    const Tooltip = /** @type {{bootstrap?: {Tooltip: {new(el: Element, opts?: object): {dispose(): void}}}}} */ (
      window
    ).bootstrap?.Tooltip;
    if (!Tooltip) return;
    /** @type {{dispose: Function}[]} */
    this._tooltipInstances = [];
    const els = this.renderRoot?.querySelectorAll('[data-bs-toggle="tooltip"]') || [];
    for (const el of els) {
      this._tooltipInstances.push(new Tooltip(el, { container: 'body' }));
    }
  }

  _disposeTooltips() {
    if (this._tooltipInstances) {
      this._tooltipInstances.forEach((t) => {
        t.dispose();
      });
      this._tooltipInstances = undefined;
    }
  }

  /** @param {EquipmentItem} eq */
  _renderStatsLine(eq) {
    if (eq.weapon && eq.category === 'weapon') {
      const parts = [];
      if (eq.weapon.damage) {
        parts.push(`${eq.weapon.damage.dice} ${eq.weapon.damage.type}`);
      }
      const props = eq.weapon.properties || {};
      for (const [key, value] of Object.entries(props)) {
        if (key === 'range' && typeof value === 'object') {
          parts.push(`Range ${value.normal}/${value.long}`);
        } else if (key === 'versatile' && typeof value === 'string') {
          parts.push(`Versatile (${value})`);
        } else if (value === true) {
          parts.push(slugToTitle(key));
        }
      }
      if (eq.weapon.mastery) {
        const isMastered = this.weaponMasterySelections.includes(eq.slug);
        const masteryDesc = MASTERY_DESCRIPTIONS[eq.weapon.mastery];
        const masteryText = `Mastery: ${slugToTitle(eq.weapon.mastery)}${isMastered ? ' \u2713' : ''}`;
        if (masteryDesc) {
          return html`<span class="card-stats"
            >${parts.length ? `${parts.join(' \u00b7 ')} \u00b7 ` : ''}${masteryText}
            <span class="mastery-info-icon" data-bs-toggle="tooltip" data-bs-title="${masteryDesc}"
              ><i class="bi bi-info-circle"></i></span
          ></span>`;
        }
        parts.push(masteryText);
      }
      return html`<span class="card-stats">${parts.join(' \u00b7 ')}</span>`;
    }
    if (eq.armor && eq.category === 'armor') {
      const parts = [];
      const ac = eq.armor.ac;
      if (ac.base) {
        let acStr = `AC ${ac.base}`;
        if (ac.addDexModifier) {
          acStr += ac.maxDexModifier ? ` + DEX (max ${ac.maxDexModifier})` : ' + DEX';
        }
        parts.push(acStr);
      } else if (ac.add) {
        parts.push(`AC +${ac.add}`);
      }
      parts.push(slugToTitle(eq.armor.category));
      if (eq.armor.stealthDisadvantage) parts.push('Stealth Disadv.');
      if (eq.armor.minStr) parts.push(`STR ${eq.armor.minStr}`);
      return html`<span class="card-stats">${parts.join(' \u00b7 ')}</span>`;
    }
    return '';
  }

  /** @param {string} slug */
  _onSell(slug) {
    this.dispatchEvent(new CustomEvent('sell-item', { detail: { slug }, bubbles: true, composed: true }));
  }

  /** @param {string} slug */
  _onBuy(slug) {
    this.dispatchEvent(new CustomEvent('buy-item', { detail: { slug }, bubbles: true, composed: true }));
  }

  _onReset() {
    this.dispatchEvent(new CustomEvent('reset-changes', { bubbles: true, composed: true }));
  }
}

customElements.define('equipment-step', EquipmentStep);
